import { useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useRepoReactions, hasUserReacted } from '@/hooks/useRepoReactions';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface ReactionButtonProps {
  repoEventId: string;
  className?: string;
}

export function ReactionButton({ repoEventId, className }: ReactionButtonProps) {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const { data: reactions = [], isLoading } = useRepoReactions({ repoEventId });
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const [isPublishing, setIsPublishing] = useState(false);

  const userReacted = hasUserReacted(reactions, user?.pubkey);
  const totalReactions = reactions.length;

  const handleLike = () => {
    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please log in with Nostr to like repositories',
        variant: 'destructive',
      });
      return;
    }

    setIsPublishing(true);

    // Create a reaction event (kind 7) with heart emoji
    publishEvent(
      {
        kind: 7,
        content: '❤️',
        tags: [
          ['e', repoEventId],
        ],
      },
      {
        onSuccess: () => {
          // Invalidate and refetch the reactions query to update UI
          queryClient.invalidateQueries({ queryKey: ['repo-reactions', repoEventId] });
          setIsPublishing(false);
        },
        onError: () => {
          setIsPublishing(false);
        },
      }
    );

    toast({
      title: 'Like sent!',
      description: 'You liked this repository',
    });
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleLike}
      className={cn(
        'h-9 w-9 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5',
        !userReacted && 'opacity-60 hover:opacity-100',
        userReacted && 'opacity-100',
        className
      )}
      disabled={isLoading || isPublishing}
    >
      {isPublishing ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Heart
          className={cn(
            'h-3.5 w-3.5',
            userReacted && 'fill-red-500 text-red-500'
          )}
        />
      )}
      {totalReactions > 0 && (
        <span className="sr-only">{totalReactions} reactions</span>
      )}
    </Button>
  );
}
